import { useCallback, useEffect, useRef } from "react";
import { useQuery } from "convex/react";
import type { FunctionReturnType } from "convex/server";
import { api } from "../../../convex/_generated/api";
import { ReportFrame, reportMeta } from "./Frame";
import { Badge } from "@/components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Money, ShareBar, SubHeading, plural } from "../kit";
import { useScrollEdges } from "../../lib/useScrollEdges";
import { cn } from "@/lib/utils";

/**
 * Report — monthly supply cost per clinic.
 *
 * Built phone-first, because a month-by-clinic grid is the classic report that
 * only works on the laptop it was designed on. At 390px the primary reading is
 * a **vertical list**: one row per clinic, biggest spender first, with the
 * month-over-month move on the same line. The full grid is underneath it in its
 * own horizontal scroller — the page body never scrolls sideways.
 *
 * Colour: rising spend is amber, falling and flat take no colour at all. Red /
 * green / gray are the fixed *status* colours from the sheet the staff already
 * read, and a green "spend went down" chip two screens away from a green
 * "ordered" pill teaches the wrong thing. Direction is carried by the glyph,
 * not the hue.
 *
 * No margin, no cost, no list price — this report is what the clinic was
 * invoiced. The query does not return those fields at all.
 */

type Data = FunctionReturnType<typeof api.reports.clinicMonthlyCost.data>;
type ClinicRow = Data["clinics"][number];

const fullDate = (ms: number): string =>
  new Date(ms).toLocaleDateString("en-CA", {
    month: "short",
    day: "numeric",
    year: "numeric",
    timeZone: "UTC",
  });

/** The month-over-month move. Percent when there is a base to divide by, the
 *  dollar move when there is not — never "+∞%". */
function TrendChip({
  row,
}: {
  row: Pick<ClinicRow, "trend" | "deltaPct" | "deltaCents">;
}) {
  // Amber is "spending went up, or we have no idea yet" — the urgency family,
  // a qualifier on a figure rather than a status. Everything else takes the
  // registry's own `secondary` badge and no colour: a fall in spend is not news
  // that needs a hue. Not kit's `<Pill>` either — a trend is a direction, and
  // red/green/gray are spoken for by request status.
  const up = row.trend === "up";
  const notable = up || row.trend === "new";

  return (
    <Badge
      variant="secondary"
      className={
        notable
          ? "gap-1 bg-amber-50 text-amber-900 ring-1 ring-amber-200 ring-inset"
          : "gap-1"
      }
    >
      {row.trend === "new" ? (
        "First month"
      ) : row.trend === "flat" ? (
        "No change"
      ) : (
        <>
          <span aria-hidden>{up ? "▲" : "▼"}</span>
          {row.deltaPct !== null ? (
            `${Math.abs(row.deltaPct)}%`
          ) : (
            <Money cents={Math.abs(row.deltaCents)} />
          )}
          <span className="sr-only">
            {up ? "up" : "down"} on the previous month
          </span>
        </>
      )}
    </Badge>
  );
}

/** Hand-drawn rather than a chart library: five polylines in a recharts
 *  ResponsiveContainer at 390px is unreadable, and this never overflows its
 *  box or animates while a screenshot is being taken. */
function Sparkline({ values, max }: { values: number[]; max: number }) {
  // A clinic that spent nothing all window draws a flat line along the bottom,
  // which on screen reads as a stray underline rather than as data. Nothing is
  // the honest thing to draw.
  if (values.length < 3 || max <= 0 || values.every((value) => value === 0)) {
    return null;
  }
  const width = 64;
  const height = 18;
  const step = width / (values.length - 1);
  const points = values
    .map((value, index) => {
      const x = index * step;
      const y = height - (value / max) * height;
      return `${x.toFixed(1)},${y.toFixed(1)}`;
    })
    .join(" ");

  return (
    <svg
      viewBox={`0 0 ${width} ${height}`}
      width={width}
      height={height}
      className="shrink-0 overflow-visible"
      aria-hidden
    >
      <polyline
        points={points}
        fill="none"
        stroke="currentColor"
        strokeWidth={1.5}
        strokeLinejoin="round"
        strokeLinecap="round"
        className="text-primary"
      />
    </svg>
  );
}

function Body({ data }: { data: Data }) {
  const { months, latest, previous, clinics, totals, maxMonthlyCents } = data;

  // Bars compare clinics *within the latest month*, which is the question the
  // top of this report answers. The sparklines use the server's window-wide max
  // so every clinic's line is drawn against the same axis.
  const maxLatest = clinics.reduce((max, c) => Math.max(max, c.latestCents), 0);

  return (
    <div className="space-y-4">
      {/* ---- Headline: the month, the number, the move ------------------ */}
      <div>
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <div className="text-sm text-muted-foreground">
              {latest ? latest.label : "No period"}
              {latest?.partial ? " · month to date" : ""}
            </div>
            <div className="num mt-0.5 text-3xl font-semibold tracking-tight">
              <Money cents={totals.latestCents} />
            </div>
          </div>
          {previous ? <TrendChip row={totals} /> : null}
        </div>

        <p className="mt-1 text-sm text-muted-foreground">
          {clinics.length} {plural(clinics.length, "clinic")} ·{" "}
          {latest?.invoiceCount ?? 0}{" "}
          {plural(latest?.invoiceCount ?? 0, "invoice")}
          {previous ? (
            <>
              {" "}
              · {previous.label} was <Money cents={totals.previousCents} />
            </>
          ) : null}
        </p>

        {latest?.partial ? (
          <p className="mt-1 text-xs text-amber-900">
            {latest.label} is only {latest.daysElapsed} of {latest.daysInMonth}{" "}
            days in — it is being compared against a full month.
          </p>
        ) : null}
      </div>

      {/* ---- Per clinic, biggest first --------------------------------- */}
      <ul className="divide-y border-t">
        {clinics.map((clinic) => (
          <li key={clinic.clinicId} className="py-3">
            <div className="flex items-baseline justify-between gap-3">
              <span className="min-w-0 truncate text-sm font-medium">
                {clinic.name}
                {clinic.active ? null : (
                  <span className="ml-1 text-xs font-normal text-muted-foreground">
                    (inactive)
                  </span>
                )}
              </span>
              <span className="num shrink-0 text-sm font-semibold">
                <Money cents={clinic.latestCents} />
              </span>
            </div>

            {/* A clinic that spent nothing gets no sliver — an empty bar is the
                honest answer, and 3% of the widest spender is the floor for
                everyone else. */}
            <ShareBar
              className="mt-1.5"
              pct={
                maxLatest > 0
                  ? Math.round((clinic.latestCents / maxLatest) * 100)
                  : 0
              }
              minPct={clinic.latestCents > 0 ? 3 : 0}
            />

            <div className="mt-1.5 flex items-center justify-between gap-2 text-xs text-muted-foreground">
              <span className="min-w-0 truncate">
                {previous ? (
                  <>
                    {previous.label} <Money cents={clinic.previousCents} />
                  </>
                ) : (
                  <>
                    {clinic.invoiceCount}{" "}
                    {plural(clinic.invoiceCount, "invoice")}
                  </>
                )}
              </span>
              <span className="flex shrink-0 items-center gap-2">
                <Sparkline values={clinic.monthlyCents} max={maxMonthlyCents} />
                {previous ? <TrendChip row={clinic} /> : null}
              </span>
            </div>
          </li>
        ))}
      </ul>

      {/* ---- The full grid, in its own scroller ------------------------ */}
      {months.length >= 2 ? <MonthGrid data={data} /> : null}
    </div>
  );
}

/**
 * The month-by-month table, in its own horizontal scroller.
 *
 * Two things it has to do that a plain `overflow-x-auto` does not:
 *
 *  1. **Open on the current month.** The columns run oldest to newest, so a
 *     left-anchored scroller opens on the months nobody asked about and hides
 *     the one being discussed. It starts scrolled to the end.
 *  2. **Say that it scrolls.** Without an edge cue, "Jun · Jul MTD" is simply
 *     missing rather than off-screen, and a clipped column on a projector is a
 *     column nobody knows to look for. The fades appear only on the side that
 *     actually has more table beyond it.
 *
 * The sticky clinic column means the labels stay put through all of it.
 */
function MonthGrid({ data }: { data: Data }) {
  const { months, clinics } = data;
  const scroller = useRef<HTMLElement | null>(null);
  // Which edge has more table beyond it is the shell's problem too, so it is
  // `useScrollEdges`. Opening on the latest month is this table's alone, and it
  // stays here — hence one ref doing two jobs. `useCallback`, so React does not
  // detach and re-attach the node on every render.
  const { setNode, atStart, atEnd } = useScrollEdges();
  /**
   * The element that actually scrolls is shadcn's, not this file's.
   *
   * `<Table>` renders its own `overflow-x-auto` wrapper around the `<table>`,
   * so once this grid moved onto the registry primitive there were two nested
   * scrollers and the inner one won. Both of this component's jobs failed
   * silently against the outer div: `scrollLeft = scrollWidth` did nothing, so
   * the grid opened on the months nobody asked about, and `useScrollEdges`
   * measured a box that never overflows, so neither fade ever appeared.
   *
   * So the ref goes on the wrapper and the scroller is found underneath it by
   * `data-slot`, which is the registry's own stable hook — React attaches a
   * host element's ref after its children are in the DOM, so the query always
   * finds it. The alternative was forking `ui/table.tsx` to take a ref.
   */
  const attach = useCallback(
    (wrapper: HTMLDivElement | null) => {
      const node =
        wrapper?.querySelector<HTMLElement>('[data-slot="table-container"]') ??
        null;
      scroller.current = node;
      setNode(node);
    },
    [setNode],
  );

  useEffect(() => {
    const element = scroller.current;
    if (!element) return;
    element.scrollLeft = element.scrollWidth;
    // Re-anchor when a month arrives; the window is live like everything else.
  }, [months.length, clinics.length]);

  /**
   * The sticky clinic column, and the one thing it costs.
   *
   * A sticky cell has to be opaque or the months scroll visibly underneath it,
   * so it paints `bg-card` — and that would punch a hole in the registry's
   * translucent `hover:bg-muted/50`, because a half-opaque wash on the row
   * cannot show through an opaque cell sitting on top of it. Rather than
   * killing the hover (which is what this table used to do), the row and the
   * sticky cell both hover to the *same opaque* token. Same colour, one paint
   * each, no notch — and the hover is the registry's, at full strength.
   */
  const stickyColumn =
    "sticky left-0 z-10 bg-card group-hover/row:bg-muted";

  return (
    <div>
      <SubHeading as="h4" className="mb-1">Month by month</SubHeading>
      {/* `-mx-6` matches the card's own content padding so the table reaches
          the card edge on a phone without the body ever scrolling sideways. The
          fades ride on this wrapper rather than inside the scroller, where they
          would scroll away with the content.
        *
        * The `px-6` gutter has to be applied *to* shadcn's scroll container —
        * it is the scrolling box, and its padding is what keeps the table off
        * the card edge at both ends of the scroll. The registry hard-codes that
        * container's class list, so the one way to reach it without forking
        * `ui/table.tsx` is its `data-slot`. */}
      <div
        ref={attach}
        className="relative -mx-6 [&_[data-slot=table-container]]:px-6"
      >
        <Table>
          <TableHeader>
            {/* No hover override needed: every header cell paints `bg-muted`
                over the row, so the registry's row hover cannot show through. */}
            <TableRow>
              <TableHead className="sticky left-0 z-10 bg-muted">
                Clinic
              </TableHead>
              {months.map((month) => (
                <TableHead key={month.key} className="bg-muted text-right">
                  {month.shortLabel}
                  {month.partial ? " MTD" : ""}
                </TableHead>
              ))}
            </TableRow>
          </TableHeader>
          <TableBody>
            {clinics.map((clinic) => (
              <TableRow key={clinic.clinicId} className="group/row hover:bg-muted">
                <TableHead className={cn(stickyColumn, "max-w-[9rem] truncate")}>
                  {clinic.name}
                </TableHead>
                {clinic.monthlyCents.map((cents, index) => (
                  <TableCell
                    key={months[index].key}
                    className={cn(
                      "num text-right",
                      cents === 0 && "text-muted-foreground",
                    )}
                  >
                    <Money cents={cents} />
                  </TableCell>
                ))}
              </TableRow>
            ))}
            {/* The totals row stays in the body rather than moving to
                `<TableFooter>`: the registry footer paints `bg-muted/50` across
                the row, and the sticky first cell has to be opaque, so the
                total would arrive as a grey band with a lighter notch cut out
                of its left end. */}
            <TableRow className="group/row font-medium hover:bg-muted">
              <TableHead className={stickyColumn}>All clinics</TableHead>
              {months.map((month) => (
                <TableCell key={month.key} className="num text-right">
                  <Money cents={month.totalCents} />
                </TableCell>
              ))}
            </TableRow>
          </TableBody>
        </Table>

        {/* Only on the side that has more table beyond it. */}
        {!atStart ? (
          <div
            aria-hidden
            className="pointer-events-none absolute inset-y-0 left-0 w-8 bg-gradient-to-r from-card to-transparent"
          />
        ) : null}
        {!atEnd ? (
          <div
            aria-hidden
            className="pointer-events-none absolute inset-y-0 right-0 w-8 bg-gradient-to-l from-card to-transparent"
          />
        ) : null}
      </div>
    </div>
  );
}

export function ClinicMonthlyCost() {
  const data = useQuery(api.reports.clinicMonthlyCost.data, {});

  // `count === 0` is what drives the frame's empty state, so it has to mean
  // "nothing was invoiced in the window" rather than "no clinics configured" —
  // five clinics that each spent nothing is an empty report, not five rows.
  //
  // The falsy test covers `null` as well as `undefined`. The frame's contract
  // says null means the query failed, and a count expression that dereferences
  // it throws before the frame ever gets to render its error state.
  const count = data
    ? data.totals.invoiceCount === 0
      ? 0
      : data.clinics.length
    : undefined;

  return (
    <ReportFrame
      {...reportMeta("clinic-monthly-cost")}
      question="Which clinic is spending more, and is it climbing?"
      data={data}
      count={count}
      empty={{
        title: "Nothing invoiced in this period",
        body: "Each clinic's monthly spend appears here as soon as a dispatch is received and invoiced.",
      }}
      footnote={
        data ? (
          <>
            Calendar months (UTC), matching the monthly statements. Each figure
            is what the clinic was invoiced, delivery and admin fees included.
            {data.emptyMonthsTrimmed > 0 && data.historyStartsAt !== null ? (
              <>
                {" "}
                Showing {data.months.length} of {data.requestedMonths} months —
                invoicing starts {fullDate(data.historyStartsAt)}.
              </>
            ) : null}
          </>
        ) : null
      }
    >
      {data ? <Body data={data} /> : null}
    </ReportFrame>
  );
}
